import { useEffect, useRef, useState } from "react";

// Диктовка прямо в приложении.
//
// Внутри — распознавание речи, встроенное в Chrome. Движок тот же, что у
// голосовой клавиатуры, но без лишнего шага «открой клавиатуру, найди микрофон»:
// сотрудник нажимает одну кнопку и говорит.
//
// Важное ограничение: распознавание идёт на серверах Google, то есть требует
// интернета. Микрофон на клавиатуре умеет работать офлайн (если скачан
// языковой пакет), поэтому при отсутствии связи мы честно отправляем туда,
// а не делаем вид, что кнопка сломалась.

/** Минимальный тип: в стандартной библиотеке TypeScript распознавания речи нет. */
type Rec = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: any) => void) | null;
  onerror: ((e: any) => void) | null;
  onend: (() => void) | null;
};

const Engine: (new () => Rec) | undefined =
  typeof window === "undefined"
    ? undefined
    : (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;

export const dictationSupported = Boolean(Engine);

const KEYBOARD_HINT = "диктуй микрофоном на клавиатуре — он работает и без интернета";

const MESSAGES: Record<string, string> = {
  "not-allowed": `Браузер не пустил к микрофону. Разреши доступ в настройках сайта или ${KEYBOARD_HINT}`,
  "service-not-allowed": `Браузер не пустил к микрофону. Разреши доступ в настройках сайта или ${KEYBOARD_HINT}`,
  "audio-capture": "Микрофон не найден",
  network: `Нет связи с распознаванием — ${KEYBOARD_HINT}`,
};

/**
 * Кнопка «Диктовать» для текстового поля.
 * `onText` вызывается готовыми кусками — форма сама решает, куда их дописать.
 */
export function useDictation(onText: (chunk: string) => void) {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState<string | null>(null);

  const rec = useRef<Rec | null>(null);
  const want = useRef(false);       // сотрудник ещё не нажал «стоп»
  const fails = useRef(0);          // подряд оборвавшихся пустых сессий
  const startedAt = useRef(0);
  const heard = useRef(false);
  const sink = useRef(onText);

  useEffect(() => { sink.current = onText; });

  // Уходим с экрана — микрофон должен погаснуть, иначе он останется висеть.
  useEffect(() => () => { want.current = false; rec.current?.abort(); }, []);

  function launch() {
    if (!Engine) return;
    const r = new Engine();
    r.lang = "ru-RU";
    r.continuous = true;
    r.interimResults = true;
    startedAt.current = Date.now();
    heard.current = false;

    r.onresult = (e: any) => {
      heard.current = true;
      fails.current = 0;
      let live = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        if (res.isFinal) sink.current(res[0].transcript);
        else live += res[0].transcript;
      }
      setInterim(live);
    };

    r.onerror = (e: any) => {
      // Молчание и наша же остановка — не ошибки: onend поднимет сессию заново.
      if (e.error === "no-speech" || e.error === "aborted") return;
      want.current = false;
      setError(MESSAGES[e.error] ?? `Распознавание не отвечает — ${KEYBOARD_HINT}`);
    };

    r.onend = () => {
      setInterim("");
      // Android обрывает распознавание после каждой фразы, даже с continuous.
      // Пока сотрудник не нажал «стоп» — поднимаем заново, иначе диктовка
      // прекращается на первой же паузе.
      const emptyAndFast = !heard.current && Date.now() - startedAt.current < 500;
      fails.current = emptyAndFast ? fails.current + 1 : 0;
      if (want.current && fails.current < 3) { launch(); return; }
      if (fails.current >= 3) {
        setError(`Микрофон не запускается — проверь разрешение в браузере или ${KEYBOARD_HINT}`);
      }
      want.current = false;
      setListening(false);
    };

    try {
      r.start();
      rec.current = r;
      setListening(true);
    } catch {
      // Предыдущая сессия ещё не закрылась — она сама вызовет onend и перезапустится.
    }
  }

  function start() {
    if (!Engine) return;
    if (!navigator.onLine) {
      setError(`Распознавание работает только с интернетом — ${KEYBOARD_HINT}`);
      return;
    }
    setError(null);
    want.current = true;
    fails.current = 0;
    launch();
  }

  function stop() {
    want.current = false;
    rec.current?.stop();
    setListening(false);
    setInterim("");
  }

  return { supported: dictationSupported, listening, interim, error, start, stop };
}
